class UspsSlider extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-usps-slider-viewport]');
    this.track = this.querySelector('[data-usps-slider-track]');
    this.previousButton = this.querySelector('[data-usps-slider-previous]');
    this.nextButton = this.querySelector('[data-usps-slider-next]');
    this.querySelector('[data-usps-slider-clone]')?.remove();
    this.slides = Array.from(this.querySelectorAll('[data-usps-slider-slide]'));
    this.currentIndex = 0;
    this.interval = Number.parseInt(this.dataset.interval, 10) || 5000;
    this.autoplay = this.dataset.autoplay === 'true';

    if (this.autoplay) {
      this.createLoopClone();
      this.update();
      this.track?.addEventListener('transitionend', this.handleTransitionEnd);
      this.start();
      this.addEventListener('mouseenter', this.stop);
      this.addEventListener('mouseleave', this.start);
      this.addEventListener('focusin', this.stop);
      this.addEventListener('focusout', this.start);
    } else {
      this.setupManualControls();
    }
  }

  disconnectedCallback() {
    this.stop();
    this.track?.removeEventListener('transitionend', this.handleTransitionEnd);
    this.loopResetTimeout && window.clearTimeout(this.loopResetTimeout);
    this.previousButton?.removeEventListener('click', this.showPreviousSlide);
    this.nextButton?.removeEventListener('click', this.showNextSlide);
    this.removeEventListener('mouseenter', this.stop);
    this.removeEventListener('mouseleave', this.start);
    this.removeEventListener('focusin', this.stop);
    this.removeEventListener('focusout', this.start);
  }

  start = () => {
    if (!this.autoplay || this.slides.length <= 1) return;

    this.stop();
    this.timer = window.setInterval(() => {
      this.goTo(this.currentIndex + 1);
    }, this.interval);
  };

  stop = () => {
    window.clearInterval(this.timer);
  };

  goTo(index) {
    if (!this.track || this.slides.length === 0) return;

    this.currentIndex = index;
    this.update();

    if (this.currentIndex === this.slides.length) {
      this.loopResetTimeout && window.clearTimeout(this.loopResetTimeout);
      this.loopResetTimeout = window.setTimeout(this.jumpToStart, 500);
    }
  }

  update() {
    if (!this.track) return;

    this.track.style.transform = `translate3d(-${this.currentIndex * 100}%, 0, 0)`;
    const activeIndex = this.currentIndex % this.slides.length;

    this.slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index === activeIndex ? 'false' : 'true');
    });
  }

  createLoopClone() {
    if (!this.track || this.slides.length <= 1) return;

    const clone = this.slides[0].cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-usps-slider-clone', '');
    clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    this.track.append(clone);
  }

  handleTransitionEnd = (event) => {
    if (event.target !== this.track || this.currentIndex !== this.slides.length) return;

    this.jumpToStart();
  };

  jumpToStart = () => {
    if (!this.track || this.currentIndex !== this.slides.length) return;

    this.loopResetTimeout && window.clearTimeout(this.loopResetTimeout);
    this.currentIndex = 0;
    this.track.style.transition = 'none';
    this.update();
    this.track.offsetHeight;
    this.track.style.transition = '';
  };

  setupManualControls() {
    if (!this.viewport) return;

    this.slides.forEach((slide) => slide.setAttribute('aria-hidden', 'false'));
    this.previousButton?.addEventListener('click', this.showPreviousSlide);
    this.nextButton?.addEventListener('click', this.showNextSlide);

    if (this.slides.length <= 1) {
      this.previousButton?.setAttribute('hidden', '');
      this.nextButton?.setAttribute('hidden', '');
    }
  }

  showPreviousSlide = () => {
    this.scrollToManualSlide(this.getManualSlideIndex() - 1);
  };

  showNextSlide = () => {
    this.scrollToManualSlide(this.getManualSlideIndex() + 1);
  };

  getManualSlideIndex() {
    if (!this.viewport || this.viewport.clientWidth === 0) return 0;

    return Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
  }

  scrollToManualSlide(index) {
    if (!this.viewport || this.slides.length === 0) return;

    const targetIndex = (index + this.slides.length) % this.slides.length;
    this.viewport.scrollTo({
      left: targetIndex * this.viewport.clientWidth,
      behavior: 'smooth',
    });
  }
}

if (!customElements.get('usps-slider-component')) {
  customElements.define('usps-slider-component', UspsSlider);
}
